/**
 * Shortcut text for the user's platform (Oleg, 2026-10-06: Windows and Linux as well as Mac). The pack writes
 * keys the Mac way (§11.6.3's tooltips, "Undo (⌘Z)"); on Windows and Linux the same bindings are Ctrl, Shift
 * and Alt, which the engine's own menus write as "Ctrl+Shift+Z". The engine's `CTRL_COMMAND` modifier (4096)
 * is ⌘ on a Mac and Ctrl elsewhere, so every binding works on both; only the text differs.
 */
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';

export interface NavigatorPlatform {
  readonly platform?: string;
  readonly userAgentData?: { readonly platform?: string };
}

/** A Mac's keyboard (macOS, and iOS or iPadOS with a keyboard), as the engine decides which key ⌘ is. */
export function isApple(nav: NavigatorPlatform | undefined = globalThis.navigator): boolean {
  const platform = nav?.userAgentData?.platform || nav?.platform || '';
  return /mac|iphone|ipad|ipod/i.test(platform);
}

let apple: boolean | undefined;

/** The platform, detected once. */
export function onApple(): boolean {
  return (apple ??= isApple());
}

/**
 * A Mac shortcut in some text, written for the platform: unchanged on a Mac; elsewhere each run of ⌘ ⇧ ⌥ and
 * its key becomes "Ctrl+Shift+Alt+Key" in the engine's order, so "Redo (⇧⌘Z)" reads "Redo (Ctrl+Shift+Z)" and
 * "⌥⌘M" reads "Ctrl+Alt+M".
 */
export function shortcutText(text: string, apple = onApple()): string {
  if (apple) return text;
  return text.replace(/([⌘⇧⌥]+)(.)/gu, (_whole, mods: string, key: string) =>
    [mods.includes('⌘') && SHEETS_STRINGS.Ctrl, mods.includes('⇧') && SHEETS_STRINGS.Shift, mods.includes('⌥') && SHEETS_STRINGS.Alt, key]
      .filter(Boolean)
      .join('+'),
  );
}
